import { useAppDispatch, useAppSelector } from "../../store/hooks";
import ganttChartStyles from "../../styles/ganttChart/ganttChart.module.css";
import VerticalGanttChartContent from "./verticalGanttChart/VerticalGanttChartContent";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  setUnplannedColumnWidth,
  setVerticalColumnWidth,
  setVerticalSvgWidth,
} from "../../store/ganttChart";
import SharedTimelineComponent from "./SharedTimelineComponent";
import VerticalTitleTextRow from "./verticalGanttChart/VerticalTitleTextRow";
import VerticalGanttChartSeparationLines from "./verticalGanttChart/VerticalGanttChartSeparationLines";
import HoverBox from "./hover/HoverBox";
import { hideTaskHoverBox } from "../../store/overlay";
import BackgroundColumns from "./verticalGanttChart/BackgroundColumns";
import { zoomInOnVerticalView, setZoomLevel } from "../../store/timeline";
import { ganttChartVerticalValues } from "../../utils/ganttChartValues";
import KpiRows from "./verticalGanttChart/KpiHeader/KpiRows";
import UnplannedTaskChartPositionAllocationService from "../../service/UnplannedTaskChartPositionAllocationService";
import {
  cancelDrag,
  setCoordinates,
  setCustomGanttChartData,
  setIntersectingIdTaskPlanAndPlacement,
  setScrollPosition,
  setTransitioning,
  TaskBeingDraggedObject,
} from "../../store/dragAndDrop";
import useGanttChartElements from "../../hooks/useGanttChartElements";
import useWindowResize from "../../hooks/useWindowResize";
import TaskIntersector from "../../service/IntervalTree";
import EmployeeTeamsComponent from "./EmployeeTeamsComponent";
import { throttle } from "lodash";
import { startAutoScroll, stopAutoScroll } from "../../service/autoScroll";
import {
  IntersectionDetails,
  createIntersectionDetails,
} from "../../types/dragAndDrop";
import DragAndDropService from "../../service/dragAndDrop";
import {
  createShiftLocation,
  createUnplannedLocation,
} from "../../service/DragAndDropApi";
import useDragAndDropPermission from "../../hooks/useDragAndDropPermission";
import useDataSelector from "../../hooks/useDataSelector";
import DragTelemetryService from "../../service/DragTelemetryService";
import { isShiftLocation } from "../../service/DragAndDropApi";
import { DragDropTarget } from "../../types/dragTelemetry";

const GanttChartWrapper = () => {
  const svgTitleRow = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();

  const {
    ganttChartSvg,
    svgWrapper,
    scrollViewContainer,
    scrollViewBoundingRect,
  } = useGanttChartElements();
  const { windowWidth } = useWindowResize();
  const { isDragAndDropAllowed } = useDragAndDropPermission();

  const {
    verticalColumnWidth,
    verticalSvgWidth,
    translateXValueInPercent,
    teamGroupColorMaps,
    unplannedColumnWidth,
    selectedGanttChartTab,
  } = useAppSelector((state) => state.ganttChartSlice);

  const {
    pixelsPerHour,
    timelineRange,
    distanceFromTop,
    pixelPerMinute,
    zoomLevel,
  } = useAppSelector((state) => state.timelineSlice);

  const {
    isDragging,
    customGanttChartData,
    taskBeingDragged,
    isTransitioning,
    isDragConfirmed,
    scrollPosition,
  } = useAppSelector((state) => state.dragAndDropSlice);
  const { taskIsHovered } = useAppSelector((state) => state.overlaySlice);
  const [containerWidthOffset, setContainerWidthOffset] = useState<number>(0);
  const [svgWrapperWidth, setSvgWrapperWidth] = useState<number>(0);
  const [unplannedTaskWidthPx, setUnplannedTaskWidthPx] = useState<number>(0);
  const [taskIntersector, setTaskIntersector] = useState<TaskIntersector>();
  const { dataToUse } = useDataSelector();
  const [svgWrapperCurrent, setSvgWrapperCurrent] = useState<HTMLDivElement>();
  /* Tanja todo: come back and check up on scroll function on load */
  /**
   * Handle initial scroll on load
   */
  useEffect(() => {
    /* TODO add logic to check if Im in editmode - and if so, only scroll to top, if there is no scroll position in store.  */
    if (timelineRange && pixelsPerHour && svgWrapper.current) {
      let top =
        new Date(timelineRange.firstHourInView).getHours() * pixelsPerHour -
        pixelsPerHour / 2;
      let left = 0;

      /* Use scrollPosition when in edit mode, to avoid the view to jump on every drag and drop event. */
      if (scrollPosition) {
        top = scrollPosition.top;
        left = scrollPosition.left;
        if (scrollPosition.zoomLevel) {
          dispatch(setZoomLevel(scrollPosition.zoomLevel));
        }
      }

      svgWrapper.current.scrollTo({
        top: top,
        left: left,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineRange, svgWrapper.current, pixelsPerHour]);

  useEffect(() => {
    if (distanceFromTop && svgWrapper.current) {
      svgWrapper.current.scrollTo({
        top: distanceFromTop,
        left: 0,
      });
    }
  }, [distanceFromTop]);

  useEffect(() => {
    const currentViewPort = document.getElementById("viewport-for-gantt-chart");
    const handleScroll = () => {
      dispatch(hideTaskHoverBox());
      if (svgWrapper.current && ganttChartSvg.current) {
        if (selectedGanttChartTab === "Custom") {
          dispatch(
            setScrollPosition({
              left: svgWrapper.current.scrollLeft,
              top: svgWrapper.current.scrollTop,
              zoomLevel,
            }),
          );
        }
      }
    };

    if (svgWrapperCurrent && currentViewPort) {
      svgWrapperCurrent.addEventListener("scroll", handleScroll);
      currentViewPort.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (svgWrapperCurrent && currentViewPort) {
        svgWrapperCurrent.removeEventListener("scroll", handleScroll);
        currentViewPort.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  /**
   * Set and update width of svg to trigger calculation and placement of all ganttChart elements
   */
  useEffect(() => {
    if (svgWrapper.current) {
      const verticalSvgWrapperWidth = svgWrapper.current.clientWidth;
      setSvgWrapperCurrent(svgWrapper.current);

      setSvgWrapperWidth(verticalSvgWrapperWidth);
      setContainerWidthOffset(
        svgWrapper.current.offsetWidth - svgWrapper.current.clientWidth,
      );
    }
  }, [svgWrapper, verticalColumnWidth, windowWidth]);

  /**
   * Find width of column and svg element based on number of employees.
   */
  useEffect(() => {
    // Todo Tanja: it seems that that the svgWrapperWidth is not triggered by a windowresize, because the svgWrapper is not changed.
    // At least, it is set as a ref, and there is some weird trail that needs to be followed from when we set that, to defining the width of the whole thing: employees plus unplannedColumnWidth
    if (svgWrapperWidth && dataToUse) {
      /* Everytime either the data, the width of the unplanned column or the width of the window changes, 
      we need to find the width of the div surrounding the svg and the number of employees to calculate svgWidth and VerticalColumnWidth.  */
      const ganttChartPlottingPoints =
        UnplannedTaskChartPositionAllocationService.getUnplannedDataPointsForPlotting(
          dataToUse,
          svgWrapperWidth,
        );

      // If there is no unplanned tasks or no overlapping unplannedtasks
      if (ganttChartPlottingPoints.highestGanttChartPosition === 0) {
        dispatch(
          setUnplannedColumnWidth(
            ganttChartVerticalValues.unplannedColumnMinWidth,
          ),
        );
        setUnplannedTaskWidthPx(
          ganttChartVerticalValues.unplannedColumnMinWidth - 2,
        );
      } else if (
        ganttChartPlottingPoints.numberOfUnplannedFittingDefaultWidth >
        ganttChartPlottingPoints.highestGanttChartPosition
      ) {
        dispatch(
          setUnplannedColumnWidth(
            ganttChartPlottingPoints.defaultWidthIfEquallyDivided,
          ),
        );
        setUnplannedTaskWidthPx(
          ganttChartPlottingPoints.defaultWidthIfEquallyDivided /
            (ganttChartPlottingPoints.highestGanttChartPosition + 1) -
            4 / (ganttChartPlottingPoints.highestGanttChartPosition + 1),
        );
      } else {
        const unplannedColumnWidth =
          (ganttChartVerticalValues.unplannedMinWidth + 1) *
            (ganttChartPlottingPoints.highestGanttChartPosition + 1) +
          4; //the 4 is just for buffer around.
        dispatch(setUnplannedColumnWidth(unplannedColumnWidth));
        setUnplannedTaskWidthPx(ganttChartVerticalValues.unplannedMinWidth);
      }

      if (unplannedColumnWidth) {
        const fullWidthDiv = document.getElementById(
          "viewport-for-gantt-chart",
        );
        const pixelsAvailableInViewForEachEmployeeColumn =
          (fullWidthDiv!.clientWidth -
            unplannedColumnWidth -
            ganttChartVerticalValues.timelineColumn) /
          ganttChartPlottingPoints.numberOfEmployees;

        /* If it is possible to fit all employee columns into the view, 
    set the global width of the svg to the size of the defaultWrapper,
     and set the columnWidth as the width available for each.
    Else, we need to expand the svg view to overflow the window
     and each column to be the minimum allowed width for an employeeColumn */
        if (
          pixelsAvailableInViewForEachEmployeeColumn >=
          ganttChartVerticalValues.verticalViewMinColumnWidth
        ) {
          dispatch(setVerticalSvgWidth(svgWrapperWidth));
          dispatch(
            setVerticalColumnWidth(
              (fullWidthDiv!.clientWidth -
                unplannedColumnWidth -
                ganttChartVerticalValues.timelineColumn) /
                ganttChartPlottingPoints.numberOfEmployees,
            ),
          );
        } else {
          dispatch(
            setVerticalSvgWidth(
              unplannedColumnWidth +
                ganttChartPlottingPoints.numberOfEmployees *
                  ganttChartVerticalValues.verticalViewMinColumnWidth +
                75,
            ),
          );

          dispatch(
            setVerticalColumnWidth(
              ganttChartVerticalValues.verticalViewMinColumnWidth,
            ),
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataToUse, unplannedColumnWidth, svgWrapperWidth, windowWidth]);

  const [svgHeight, setSvgHeight] = useState<number>();

  useEffect(() => {
    if (pixelsPerHour && timelineRange && svgTitleRow.current) {
      setSvgHeight(timelineRange?.fullDayHours * pixelsPerHour);
    }
  }, [pixelsPerHour, timelineRange, svgTitleRow]);

  /**
   * Handle drag and drop
   */

  const [moveTaskCommandObject, setMoveTaskCommandObject] =
    useState<IntersectionDetails>();
  const submittedDragRef = useRef<TaskBeingDraggedObject>();

  const [ganttChartSvgWrapper, setGanttChartSvgWrapper] = useState<
    DOMRect | undefined
  >();

  const [ganttChartSvgElement, setGanttChartSvgElement] = useState<
    DOMRect | undefined
  >();

  const handleMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    // Don't allow interactions during transitions or if required refs are unavailable
    if (
      isTransitioning ||
      !scrollViewContainer.current ||
      !svgWrapper.current ||
      !scrollViewBoundingRect
    ) {
      if (isTransitioning) {
        console.warn("Cannot start drag: component is transitioning");
      } else {
        console.warn("Cannot start auto scroll: missing required DOM elements");
      }
      return;
    }

    const { clientX, clientY } = getEventCoordinates(e);

    const rect = ganttChartSvg.current!.getBoundingClientRect();
    const newCoords = {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
    dispatch(setCoordinates(newCoords));
  };

  const throttledDispatch = useCallback(
    throttle((coordinates) => {
      // Check if dragging over unplanned area (left of the first shift column)
      const unplannedBoundary =
        unplannedColumnWidth + ganttChartVerticalValues.timelineColumn;

      if (coordinates.x < unplannedBoundary) {
        // Dropping in unplanned area - no task intersection needed
        dispatch(
          setIntersectingIdTaskPlanAndPlacement({
            isCloserToStart: false,
            destinationTaskIdBefore: null,
            destinationTaskIdAfter: null,
          }),
        );

        const destinationLocation = createUnplannedLocation();
        const intersectionDetails = createIntersectionDetails(
          null, // No before/after tasks in unplanned area
          null,
          destinationLocation,
        );
        setMoveTaskCommandObject(intersectionDetails);
        DragTelemetryService.recordTarget(intersectionDetails);
      } else if (taskIntersector) {
        // Dropping in shift area - use existing intersection logic
        let intersectionResult = taskIntersector.intersect(
          coordinates.x,
          coordinates.y,
          taskBeingDragged?.task.idTask, // Exclude the dragged task from intersection calculations
        );

        // if task intersect with itself, move indicatorLine to alternative task.
        if (
          intersectionResult &&
          taskBeingDragged?.task.idTask ===
            intersectionResult?.intersectingTask?.idTask
        ) {
          intersectionResult = {
            ...intersectionResult,
            targetTaskForIndicator: intersectionResult?.taskAbove
              ? intersectionResult?.taskAbove
              : intersectionResult?.taskBelow,
            showIndicatorBefore: intersectionResult?.taskAbove ? false : true,
            intersectingTask: null,
          };
        }

        if (intersectionResult) {
          const intersectingTaskData = DragAndDropService.findIntersectionData(
            intersectionResult,
            coordinates.y,
          );

          dispatch(
            setIntersectingIdTaskPlanAndPlacement({
              intersectingTaskId: intersectionResult.intersectingTask?.idTask,
              targetTaskId: intersectionResult.targetTaskForIndicator?.idTask,
              isCloserToStart: intersectionResult.showIndicatorBefore,
              destinationTaskIdBefore:
                intersectingTaskData.destinationTaskIdBefore,
              destinationTaskIdAfter:
                intersectingTaskData.destinationTaskIdAfter,
            }),
          );

          /* Regardless of mouse position, intersectionResult always contain at least either a before or after task, 
          but not always an intersecting task. Therefore, we need to find the shiftID from either above or below. */
          const destinationShiftIndex = DragAndDropService.findShiftData(
            unplannedColumnWidth,
            verticalColumnWidth,
            coordinates.x,
          );

          const destinationShiftId =
            dataToUse?.employees[destinationShiftIndex].shiftSchedule.idShift;

          if (destinationShiftId) {
            const destinationLocation = createShiftLocation(destinationShiftId);
            const intersectionDetails = createIntersectionDetails(
              intersectingTaskData.destinationTaskIdBefore,
              intersectingTaskData.destinationTaskIdAfter,
              destinationLocation,
            );
            setMoveTaskCommandObject(intersectionDetails);
            DragTelemetryService.recordTarget(intersectionDetails);
          }
        }
      }
    }, 120), // ~60fps, adjust as needed

    [
      taskIntersector,
      dispatch,
      taskBeingDragged,
      unplannedColumnWidth,
      verticalColumnWidth,
      dataToUse,
    ],
  );

  const getEventCoordinates = (e: React.MouseEvent | React.TouchEvent) => {
    if ("touches" in e) {
      const touch = e.touches[0] || e.changedTouches[0];
      return { clientX: touch.clientX, clientY: touch.clientY };
    }
    return { clientX: e.clientX, clientY: e.clientY };
  };

  const handleMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (
      !isDragAndDropAllowed ||
      !taskIntersector ||
      !isDragging ||
      !ganttChartSvgWrapper ||
      !ganttChartSvgElement ||
      !ganttChartSvg.current
    ) {
      return;
    }

    const scrollContainer = document.getElementById("outer-scroll-wrapper");

    if (scrollContainer && isDragging) {
      startAutoScroll(
        scrollContainer,
        scrollContainer,
        () => scrollContainer.getBoundingClientRect(),
        () => scrollContainer.getBoundingClientRect() || new DOMRect(),
      );
    }

    const { clientX, clientY } = getEventCoordinates(e);

    const rect = ganttChartSvg.current.getBoundingClientRect();
    const newCoords = {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
    dispatch(setCoordinates(newCoords));
    throttledDispatch(newCoords);
  };

  const handleMouseUp = () => {
    // Only execute drop command if drag was confirmed (not just a click)
    if (isDragging && taskBeingDragged && isDragConfirmed) {
      handleDropCommand();
    } else if (isDragging) {
      DragTelemetryService.recordIgnoredRelease(
        taskBeingDragged ? "NOT_CONFIRMED" : "NO_TASK",
      );
    }
  };

  // A release that cannot drop must still end the drag; otherwise the visit
  // stays on the cursor and the next click drops it wherever the cursor is.
  const cancelDragOnRelease = useCallback(() => {
    DragTelemetryService.finish("CANCELLED");
    dispatch(cancelDrag());
    stopAutoScroll();
  }, [dispatch]);

  const handleDropCommand = async () => {
    if (!moveTaskCommandObject) {
      // No drop target yet, e.g. a press-and-hold drag released before any move
      DragTelemetryService.recordIgnoredRelease("NO_COMMAND");
      cancelDragOnRelease();
      return;
    }
    if (!taskBeingDragged || submittedDragRef.current === taskBeingDragged)
      return;

    // Touch and mouse releases can arrive before the response clears drag state.
    submittedDragRef.current = taskBeingDragged;

    const dropTarget: DragDropTarget = {
      toShiftId: isShiftLocation(moveTaskCommandObject.destinationLocation)
        ? moveTaskCommandObject.destinationLocation.shiftId
        : null,
      beforeTaskId: moveTaskCommandObject.destinationWorkTaskIdBefore,
      afterTaskId: moveTaskCommandObject.destinationWorkTaskIdAfter,
    };
    const telemetry = DragTelemetryService.markDropSent();

    const result = await DragAndDropService.executeDropCommand(
      {
        taskBeingDragged: taskBeingDragged!,
        moveTaskCommandObject: moveTaskCommandObject!,
        customGanttChartData,
      },
      dataToUse,
      dispatch,
      selectedGanttChartTab,
      telemetry,
    );

    setMoveTaskCommandObject(undefined);

    if (result.success && result.data) {
      dispatch(setCustomGanttChartData(result.data.state!));
      // Two frames: the first runs before the new state is painted, the second after
      requestAnimationFrame(() =>
        requestAnimationFrame(() =>
          DragTelemetryService.finish("DROPPED", dropTarget, telemetry),
        ),
      );
    } else {
      DragTelemetryService.finish("FAILED", dropTarget, telemetry);
    }

    stopAutoScroll();
  };

  // onMouseUp is only on the chart, so a release over the header, KPI bar or
  // outside the window would otherwise leave the drag running.
  useEffect(() => {
    if (!isDragging) return;

    const handleReleaseOutsideChart = (e: MouseEvent) => {
      if (ganttChartSvg.current?.contains(e.target as Node)) return;
      cancelDragOnRelease();
    };

    window.addEventListener("mouseup", handleReleaseOutsideChart);
    return () =>
      window.removeEventListener("mouseup", handleReleaseOutsideChart);
  }, [isDragging, cancelDragOnRelease, ganttChartSvg]);

  useEffect(() => {
    if (timelineRange && dataToUse) {
      // Set transitioning state to prevent interactions during setup
      dispatch(setTransitioning(true));

      // Use setTimeout to ensure DOM has updated before calculating bounds
      const updateBoundsAndIntersector = () => {
        // Only proceed if all required elements and data are available
        if (
          !svgWrapper.current ||
          !ganttChartSvg.current ||
          !dataToUse ||
          !timelineRange
        ) {
          console.warn(
            "Cannot update bounds: missing required elements or data",
          );
          dispatch(setTransitioning(false));
          return;
        }

        const svgWrapperBounds = svgWrapper.current.getBoundingClientRect();
        const svgElementBounds = ganttChartSvg.current.getBoundingClientRect();

        setGanttChartSvgWrapper(svgWrapperBounds);
        setGanttChartSvgElement(svgElementBounds);

        setTaskIntersector(
          new TaskIntersector(
            dataToUse.employees,
            timelineRange,
            pixelPerMinute,
            ganttChartVerticalValues.timelineColumn,
            unplannedColumnWidth,
            verticalColumnWidth,
          ),
        );

        // Setup complete, allow interactions
        dispatch(setTransitioning(false));
      };

      // Delay bounds calculation to allow DOM to settle
      const timeoutId = setTimeout(updateBoundsAndIntersector, 100); // Increased delay for stability

      return () => {
        clearTimeout(timeoutId);
        dispatch(setTransitioning(false));
      };
    }
  }, [
    timelineRange,
    dataToUse,
    pixelPerMinute,
    unplannedColumnWidth,
    verticalColumnWidth,
  ]);

  const groupsAreAvailable =
    dataToUse &&
    dataToUse.useColorGroupingsAsTeamSplitter &&
    !!teamGroupColorMaps;

  return (
    <>
      {dataToUse && (
        <div id="outer-scroll-wrapper" className="h-full w-full overflow-auto">
          <div
            style={{
              top: "100px",
              display: "flex",
              flexDirection: "column",
              height: "auto",
              minHeight: "100%",
              width: "100%",
            }}
            id="viewport-for-gantt-chart"
            ref={scrollViewContainer} //TODO: Update drag and drop scroll to work on  id="outer-scroll-wrapper"
          >
            {groupsAreAvailable && (
              <EmployeeTeamsComponent
                teamGroupColorMaps={teamGroupColorMaps}
                columnWidth={verticalColumnWidth}
                leftOffset={
                  unplannedColumnWidth + ganttChartVerticalValues.timelineColumn
                }
              />
            )}
            <div
              className=" w-full"
              style={{
                top: groupsAreAvailable ? "27px" : 0,
                zIndex: 100, // Use style for z-index instead of z-100 class
                position: "sticky", // Ensure proper stacking context
                // A release over the header never reaches the chart, so the drop was lost.
                pointerEvents: isDragging ? "none" : undefined,
              }}
              ref={svgTitleRow}
            >
              <VerticalTitleTextRow
                title="medarb."
                employees={dataToUse.employees}
                solutionType={dataToUse.solutionType}
              />
              <KpiRows employees={dataToUse.employees} />
            </div>
            <div
              ref={svgWrapper}
              id="vertical-ganttChart-svg-wrapper"
              style={{
                flex: 1,
                minHeight: 0,
                borderTop: "1px solid var(--col-border-primary)",
                width: "fit-content",
                minWidth: `calc(100% + ${containerWidthOffset}px`,
                marginTop: "34px", //to account for height on folded KPI bar.
              }}
              className={`${ganttChartStyles.verticalSvgWrapper} `}
            >
              {verticalSvgWidth && verticalColumnWidth && (
                <>
                  <div
                    ref={ganttChartSvg}
                    id="vertical-ganttChart-svg"
                    style={{
                      position: "relative",
                      width: verticalSvgWidth,
                      height: svgHeight,
                      transform: `translateX(-${translateXValueInPercent * verticalSvgWidth}px)`,
                      transition: "transform 250ms ease",
                    }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMove}
                    onMouseUp={handleMouseUp}
                    onTouchStart={handleMouseDown}
                    onTouchMove={handleMove}
                    onTouchEnd={handleMouseUp}
                  >
                    {timelineRange && (
                      <BackgroundColumns
                        verticalColumnWidth={verticalColumnWidth}
                        ganttChartData={dataToUse}
                      />
                    )}
                    <SharedTimelineComponent />
                    {pixelsPerHour && verticalColumnWidth && (
                      <VerticalGanttChartSeparationLines
                        employees={dataToUse.employees}
                        verticalColumnWidth={verticalColumnWidth}
                        pixelsPerHour={pixelsPerHour}
                      />
                    )}
                    <VerticalGanttChartContent
                      unplannedTaskWidth={unplannedTaskWidthPx}
                      unplannedColumnWidth={unplannedColumnWidth}
                    />
                    {taskIsHovered && !isDragging && <HoverBox />}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GanttChartWrapper;
